import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import useStyles from './styles'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import VideoThumbnail from './VideoThumbnail'
import VideoOverlay from './VideoOverlay'

import { getFacebookVideoPicture } from './helpers'

import {
  facebookImageUrlsSelector,
  facebookRequestedVideoIdsSelector,
  facebookVideoUrlsSelector
} from '../../../../../../modules/selectors/mediaFiles'

import { getFacebookVideoUrls } from '../../../../../../modules/actions/mediaFiles'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { AdFacebookPreviewContext } from '../../../../AdFacebookPreview/AdFacebookPreviewContextProvider'
import { FILE_ID, FILE_NAME, FILE_URL } from '../../../../../../forms/ReusableFormFields/AdFileUpload/fields'

const FacebookVideoPreview = ({
  viewAdOnFacebook,
  className = '',
  videoPictureClassName,
  mediaFile = {},
  onLoad,
  pauseFromOutside = false,
  showControls = false,
  mediaThumbnailImageRatio
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const imageUrls = useSelector(facebookImageUrlsSelector)
  const videoUrls = useSelector(facebookVideoUrlsSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const requestedVideoIds = useSelector(facebookRequestedVideoIdsSelector)
  const adContext = useContext(AdFacebookPreviewContext)
  const adAccountId = adContext?.adAccountId || selectedAdAccountId

  const videoRef = useRef(null)

  const videoPicture = getFacebookVideoPicture(mediaFile, imageUrls)
  const { [FILE_URL]: mediaUrl, [FILE_NAME]: fileName } = mediaFile

  const requestedVideoUrlData = videoUrls.find(video => video.id === mediaFile[FILE_ID])

  // Initially we don't show video element, and show video picture instead
  const [showVideoElement, setShowVideoElement] = useState(false)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  const videoOnLoadHandler = useCallback(() => {
    setIsVideoLoaded(true)

    onLoad && onLoad()
  }, [onLoad, setIsVideoLoaded])

  const togglePlay = useCallback(() => {
    const video = videoRef?.current
    if (video) {
      if (isPaused) {
        video.play()
        setIsPaused(false)
      } else {
        video.pause()
        setIsPaused(true)
      }
    }
  }, [isPaused])

  const onInitialPlayPress = useCallback(() => {
    setShowVideoElement(true)
    // Usually we don't fetch video info, and represent videoPicture instead,
    // we fetch video info only when users press "play".
    // (There is one exception, we also fetch video info in edit ad form to be able to represent fileName)
    if (!requestedVideoIds.includes(mediaFile[FILE_ID])) {
      dispatch(
        getFacebookVideoUrls([
          {
            account: adAccountId,
            video_ids: [mediaFile[FILE_ID]]
          }
        ])
      )
    }
  }, [dispatch, adAccountId, setShowVideoElement, requestedVideoIds, mediaFile])

  useEffect(() => {
    if (showVideoElement && pauseFromOutside && !isPaused) {
      videoRef?.current?.pause()
      setIsPaused(true)
    }
  }, [pauseFromOutside, videoRef, showVideoElement, isPaused])

  useEffect(() => {
    // If video url was loaded, but it has 'iframe_src=true' - we can't show the video
    // we decided to open the ad preview on facebook in new tab
    if (showVideoElement && requestedVideoUrlData?.iframe_src && viewAdOnFacebook) {
      window.open(viewAdOnFacebook, '_blank', 'noopener,noreferrer')
    }
  }, [showVideoElement, requestedVideoUrlData, viewAdOnFacebook])

  return (
    <div className={classnames(classes.container, className)}>
      <VideoOverlay
        showVideoElement={showVideoElement}
        isVideoLoaded={isVideoLoaded}
        isPaused={isPaused}
        togglePlay={togglePlay}
        requestedVideoUrlData={requestedVideoUrlData}
        classes={classes}
        onInitialPlayPress={onInitialPlayPress}
        // no need to show loader if iframe_src is true because we open the ad preview on facebook
        hideLoader={requestedVideoUrlData?.iframe_src}
      />

      {/*still show image until the video is loaded*/}
      {!isVideoLoaded && (
        <VideoThumbnail
          videoPicture={videoPicture}
          fileName={fileName}
          onLoad={onLoad}
          mediaThumbnailImageRatio={mediaThumbnailImageRatio}
          videoPictureClassName={videoPictureClassName}
        />
      )}

      {/*don't show video element until user clicks on the thumbnail*/}
      {showVideoElement && (
        <video
          // don't show the video until it's loaded to avoid flickering
          style={{ display: showVideoElement && isVideoLoaded ? 'block' : 'none' }}
          onLoadedData={videoOnLoadHandler}
          // video element should have src too! In other case when mediaUrl is changed - it doesn't change the video
          // because of memoization
          src={mediaUrl}
          ref={videoRef}
          autoPlay={true}
          loop={true}
          controls={showControls}
        >
          <source src={mediaUrl} />
        </video>
      )}
    </div>
  )
}

FacebookVideoPreview.propTypes = {
  viewAdOnFacebook: PropTypes.string,
  className: PropTypes.string,
  videoPictureClassName: PropTypes.string,
  mediaFile: PropTypes.object,
  onLoad: PropTypes.func,
  pauseFromOutside: PropTypes.bool,
  showControls: PropTypes.bool,
  showPlayPauseButtonOnHover: PropTypes.bool
}

export default FacebookVideoPreview
